/**
 * Fetches the authenticated user's profile, plan, and usage from /me.
 *
 * The header, plan badges, and trial countdown all read from this. We share
 * a single TanStack Query so all consumers get the same data without N
 * fetches per page render.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiHttpError } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';

export type PlanTier = 'free' | 'entry' | 'pro' | 'agency';

export interface PlanLimits {
  domains: number;
  scansPerDomainPerMonth: number;
  blocklistSlugs: string[];
  alerts: boolean;
  dmarcIngest: boolean;
  postmaster: boolean;
  publicReports: boolean;
  scanHourlyLimit: number;
}

export interface MeResponse {
  user: { id: string; email: string; appRole: 'user' | 'admin' | 'moderator' };
  org: { id: string; role: 'owner' | 'admin' | 'analyst' };
  plan: {
    tier: PlanTier;
    trialing: boolean;
    trialExpired: boolean;
    trialEndsAt: string | null;
    trialDaysRemaining: number | null;
    limits: PlanLimits;
  };
  usage: {
    domains: number;
    domainsLimit: number;
  };
  catalog: Record<PlanTier, PlanLimits>;
  stripePublishableKey: string | null;
}

export function useMe() {
  const auth = useAuth();
  return useQuery<MeResponse, ApiHttpError>({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/me'),
    enabled: !!auth.user,
    // /me is cheap and important for header state — keep it fresh on focus.
    staleTime: 60_000,
  });
}
